/**
 * m3 echo Frontend - Benchmark-Aufstellung (Admin, Tab "Modellpflege")
 *
 * Der Admin pflegt die zentrale Modellaufstellung als lokalen Entwurf,
 * sieht Kostenvorschau, Stabilität und Warnungen und übernimmt sie danach.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../../services/api';
import { invalidateModelCatalog } from '../../hooks/useModelCatalog';
import type {
  BenchmarkCostPreview,
  BenchmarkLineupModel,
  GroundingTestResult,
} from '../../types/api';
import BenchmarkCostPanel from './BenchmarkCostPanel';

const PREVIEW_DEBOUNCE_MS = 400;

const STABILITY_HINT = 'Letzte 7 Tage aus Benchmarks und Custom-Autopiloten (ohne Modellvergleich)';

const PRICE_LABELS: Record<string, string> = {
  ok: 'Preis vorhanden',
  fehlt: 'Preis fehlt',
  per_response: 'Preis je Antwort',
};

const healthColor = (status: string | null): string =>
  status === 'healthy' ? '#4caf50' : status === 'unhealthy' ? '#f44336' : '#9e9e9e';

const errorDetail = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const sameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((c) => b.includes(c));

interface BenchmarkLineupSectionProps {
  /** Erhöhen, wenn sich Modelle oder Preise außerhalb dieser Sektion geändert haben. */
  reloadSignal?: number;
  /** Wird nach erfolgreichem „Übernehmen“ aufgerufen. */
  onApplied?: () => void;
}

const BenchmarkLineupSection: React.FC<BenchmarkLineupSectionProps> = ({
  reloadSignal = 0,
  onApplied,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const [models, setModels] = useState<BenchmarkLineupModel[]>([]);
  const [lineup, setLineup] = useState<string[]>([]);
  const [draft, setDraft] = useState<string[]>([]);
  const [loadError, setLoadError] = useState('');

  const [preview, setPreview] = useState<BenchmarkCostPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewSeq = useRef(0);

  const [groundingResults, setGroundingResults] = useState<Record<string, GroundingTestResult>>({});
  const [groundingTesting, setGroundingTesting] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadSeq = useRef(0);

  // keepDraft: ungespeicherten Entwurf und Websuche-Ergebnisse behalten,
  // soweit die Modelle noch aktiv sind (Reload nach Änderungen im Tab).
  const load = useCallback(async (keepDraft: boolean) => {
    const seq = ++loadSeq.current;
    try {
      const res = await settingsApi.getBenchmarkLineup();
      if (seq !== loadSeq.current) return;
      const activeCodes = new Set(res.data.models.map((m) => m.model_code));
      setModels(res.data.models);
      setLineup(res.data.lineup);
      setDraft((prev) => (keepDraft ? prev.filter((c) => activeCodes.has(c)) : res.data.lineup));
      setGroundingResults((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([code]) => activeCodes.has(code)))
      );
      setLoadError('');
    } catch (error) {
      if (seq !== loadSeq.current) return;
      setLoadError(errorDetail(error, 'Fehler beim Laden der Benchmark-Aufstellung'));
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const lastReloadSignal = useRef(reloadSignal);
  useEffect(() => {
    if (reloadSignal === lastReloadSignal.current) return;
    lastReloadSignal.current = reloadSignal;
    load(true);
  }, [reloadSignal, load]);

  // Kostenvorschau: entprellt bei jeder Entwurfsänderung neu berechnen
  useEffect(() => {
    if (draft.length === 0) {
      previewSeq.current += 1;
      setPreview(null);
      setPreviewError('');
      setPreviewLoading(false);
      return undefined;
    }
    const seq = ++previewSeq.current;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await settingsApi.previewBenchmarkCost(draft);
        if (seq !== previewSeq.current) return;
        setPreview(res.data);
        setPreviewError('');
      } catch (error) {
        if (seq !== previewSeq.current) return;
        setPreviewError(errorDetail(error, 'Kostenvorschau nicht verfügbar'));
      } finally {
        if (seq === previewSeq.current) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  const nameOf = useCallback(
    (code: string) => models.find((m) => m.model_code === code)?.display_name || code,
    [models]
  );

  const toggle = (code: string) =>
    setDraft((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const handleGroundingTest = async (code: string) => {
    setGroundingTesting(code);
    try {
      const res = await settingsApi.testModelGrounding(code);
      setGroundingResults((prev) => ({ ...prev, [code]: res.data }));
    } catch (error) {
      setGroundingResults((prev) => ({
        ...prev,
        [code]: {
          passed: false,
          response_excerpt: '',
          sources_count: 0,
          usage_present: false,
          error: errorDetail(error, 'Websuche-Test fehlgeschlagen'),
        },
      }));
    } finally {
      setGroundingTesting(null);
    }
  };

  const handleApply = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await settingsApi.setBenchmarkLineup(draft);
      setConfirmOpen(false);
      await load(false);
      invalidateModelCatalog(queryClient);
      onApplied?.();
    } catch (error) {
      setSaveError(errorDetail(error, 'Fehler beim Speichern der Aufstellung'));
    } finally {
      setSaving(false);
    }
  };

  const warnings = useMemo(() => {
    const result: string[] = [];
    const drafted = models.filter((m) => draft.includes(m.model_code));
    const byProvider: Record<string, number> = {};
    drafted.forEach((m) => {
      byProvider[m.provider] = (byProvider[m.provider] || 0) + 1;
    });
    Object.entries(byProvider).forEach(([provider, count]) => {
      if (count > 1) result.push(`Mehr als ein Modell von ${provider}`);
    });
    drafted.forEach((m) => {
      const status = preview?.models[m.model_code]?.status;
      if (m.pricing_status === 'fehlt') {
        result.push(`Preis fehlt: ${m.display_name}`);
      } else if (m.pricing_status === 'per_response') {
        result.push(`Preis je Antwort – nicht schätzbar: ${m.display_name}`);
      } else if (status === 'unbekannt') {
        result.push(`Kosten unbekannt (keine Nutzungsdaten): ${m.display_name}`);
      }
      if (m.health_status === 'unhealthy') result.push(`Health unhealthy: ${m.display_name}`);
      const g = groundingResults[m.model_code];
      if (m.supports_grounding !== false && g && !g.passed) {
        result.push(`Websuche-Test nicht bestanden: ${m.display_name}`);
      }
    });
    return Array.from(new Set(result));
  }, [models, draft, preview, groundingResults]);

  const unchanged = sameSet(draft, lineup);
  const canApply = draft.length > 0 && !unchanged;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Benchmark-Aufstellung
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Diese Modelle werden für alle Benchmark-Abfragen verwendet.
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <BenchmarkCostPanel
        preview={preview}
        loading={previewLoading}
        error={previewError}
        modelLabel={nameOf}
      />

      {warnings.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {warnings.map((w) => (
            <Chip key={w} size="small" color="warning" label={w} />
          ))}
        </Box>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">Im Benchmark</TableCell>
              <TableCell>Hersteller</TableCell>
              <TableCell>Modell</TableCell>
              <TableCell>Health</TableCell>
              <TableCell>Websuche</TableCell>
              <TableCell>
                <Tooltip title={STABILITY_HINT}>
                  <span>7 Tage: Läufe / Fehler / Timeouts / nicht verfügbar</span>
                </Tooltip>
              </TableCell>
              <TableCell>Preis</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.map((m) => {
              const g = groundingResults[m.model_code];
              const groundingOff = m.supports_grounding === false;
              return (
                <TableRow key={m.model_code} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={draft.includes(m.model_code)}
                      onChange={() => toggle(m.model_code)}
                      inputProps={{ 'aria-label': `${m.display_name} im Benchmark` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={m.provider} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{m.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.model_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={m.health_status || 'unknown'}>
                      <FiberManualRecordIcon
                        sx={{ fontSize: 16, color: healthColor(m.health_status) }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={groundingOff ? 'Websuche für dieses Modell nicht konfiguriert' : ''}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGroundingTest(m.model_code)}
                            disabled={groundingOff || groundingTesting !== null}
                            startIcon={
                              groundingTesting === m.model_code ? <CircularProgress size={14} /> : null
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Websuche testen
                          </Button>
                        </span>
                      </Tooltip>
                      {g && !groundingOff && (
                        <Tooltip title={g.error || g.response_excerpt || ''}>
                          <Chip
                            size="small"
                            color={g.passed ? 'success' : 'error'}
                            label={g.passed ? 'bestanden' : 'nicht bestanden'}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {m.stability.runs} / {m.stability.errors} / {m.stability.timeouts} /{' '}
                    {m.stability.unavailable}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={m.pricing_status === 'ok' ? 'default' : 'warning'}
                      label={PRICE_LABELS[m.pricing_status] || m.pricing_status}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Stabilität: {STABILITY_HINT}.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button
          variant="text"
          onClick={() => setDraft(lineup)}
          disabled={unchanged}
        >
          Zurücksetzen
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setSaveError('');
            setConfirmOpen(true);
          }}
          disabled={!canApply}
        >
          Übernehmen
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Benchmark-Aufstellung übernehmen</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Neue Aufstellung: {draft.map(nameOf).join(', ')}
            </Typography>
            {preview && sameSet(preview.draft, draft) ? (
              <Typography variant="body2">
                Gilt ab dem nächsten Lauf für alle {preview.affected_autopilots} aktiven
                Autopiloten in {preview.affected_tenants} Mandanten.
              </Typography>
            ) : (
              <Typography variant="body2">Gilt ab dem nächsten Lauf für alle aktiven Autopiloten.</Typography>
            )}
          </DialogContentText>
          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleApply} variant="contained" disabled={saving}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BenchmarkLineupSection;
